import axios from "axios";
import PubSub from "pubsub-js";
import { SubmissionError } from "redux-form";
import _ from "lodash";
import { toastr } from "react-redux-toastr";
import partsApi from "MODULES/parts/api";
import {
  RESERVE_FOR_REPAIR,
  RESERVE_FOR_SHOP_ASSIST
} from "MODULES/inventories/constants";
import { stringify } from "query-string";
import { properCaseField, toSentence } from "LIB/formatters/text";
import session from "./session";
import { API_BASE } from "./constants";

export const adapter = axios.create({
  baseURL: API_BASE,
  timeout: 35000,
  withCredentials: true,
  headers: {
    Accept: "application/json"
  }
});

const releaseIdInterceptor = response => {
  if (response.headers && response.headers["x-release-id"]) {
    PubSub.publish("releaseId.read", response.headers["x-release-id"]);
  }
  return response;
};

adapter.interceptors.response.use(releaseIdInterceptor);

adapter.interceptors.response.use(undefined, error => {
  if (_.get(error, "response.status") === 403) {
    toastr.error(
      "Unauthorized",
      "Sorry, you're not authorized to perform this action."
    );
  }

  return Promise.reject(error);
});

adapter.interceptors.response.use(undefined, error => {
  if (_.get(error, "response.status") === 500) {
    toastr.error("Error", "An unexpected error occurred.");
  }

  return Promise.reject(error);
});

adapter.interceptors.response.use(undefined, error => {
  if (
    _.get(error, "response.status") === 422 &&
    _.get(error, "response.data.errors")
  ) {
    // handle invalid reserve items gracefully
    const responseURL = _.get(error, "response.request.responseURL");

    if (responseURL.includes("cart_items")) {
      toastr.error("Error", _.get(error, "response.data.message"));
    }

    if (
      _.get(error, "response.data.errors.status") ||
      _.get(error, "response.data.errors.repair_status")
    ) {
      toastr.error("Error", "Action cannot be performed on inventory");
    }

    // Assign ActiveRecord "base" error messages to redux-form's special "_errors" key.
    const errors = _.get(error, "response.data.errors");

    Object.keys(errors).forEach(errorKey => {
      if (errorKey === "base") {
        errors._error = errors.base; // eslint-disable-line no-underscore-dangle
      } else if (errorKey.endsWith(".base")) {
        const withoutBase = errorKey.substring(0, errorKey.indexOf(".base"));
        errors[`${withoutBase}._error`] = errors[`${withoutBase}.base`];
      }
    });

    throw new SubmissionError(
      _.zipObjectDeep(Object.keys(errors), Object.values(errors))
    );
  }

  return Promise.reject(error);
});

export const handleSubmissionError = error => {
  if (error instanceof SubmissionError) {
    const keys = Object.keys(error.errors);
    const msg = keys
      .map(k => {
        if (k === "base") return null;

        return `${properCaseField(k)}: ${toSentence(error.errors[k])}`;
      })
      .filter(f => f);
    toastr.error("Error", toSentence(msg));
  } else {
    // Handle other errors
    throw error;
  }
};

adapter.interceptors.response.use(
  config => config,
  error => {
    if (
      _.get(error, "response.status") === 408 ||
      error.code === "ECONNABORTED"
    ) {
      // Normalize error code for timeouts so our forms can properly handle them without knowing specific HTTP status and error codes.
      return Promise.reject(Object.assign({}, error, { code: "TIMEOUT" }));
    }
    return Promise.reject(error);
  }
);

adapter.interceptors.response.use(undefined, error => {
  if (_.get(error, "response.status") === 401) {
    PubSub.publish("session.expired");
    toastr.error(
      "Session Expired",
      "Your session has expired. Please log in again to continue."
    );

    return Promise.resolve(error);
  }

  if (_.get(error, "response.status") === 418) {
    return Promise.reject(error.response.data);
  }

  if (
    _.get(error, "response.status") === 501 &&
    _.get(error, "response.data.errors")
  ) {
    return Promise.reject(error.response.data);
  }

  return Promise.reject(error);
});

export default {
  accountingLogs: {
    search: (
      params = {
        page: 1,
        limit: 20,
        sort: "id",
        reverse: true,
        filter: true
      }
    ) =>
      adapter.post("/accounting_logs/search", {
        search: params
      })
  },
  aircrafts: {
    search: search => adapter.post("/aircraft/search", { search })
  },
  applications: {
    delete: id => adapter.delete(`/applications/${id}`)
  },
  appSetting: {
    index: () => adapter.get("/app_setting"),
    update: data =>
      adapter.put("/app_setting", { app_setting: data }).then(resp => {
        session.setSettings(resp.data.settings);
        return resp;
      })
  },
  consignments: {
    create: params => adapter.post(`/consignments`, { consignment: params }),
    export: params => adapter.get(`/consignments/export?${stringify(params)}`),
    import: (id, params) =>
      adapter.post(`/consignments/${id}/import`, { consignment: params }),
    inventories: {
      search: (id, search) =>
        adapter.post(`/consignments/${id}/inventories/search`, { search })
    },
    lineItems: {
      search: (id, search) =>
        adapter.post(`/consignments/${id}/line_items/search`, { search })
    },
    report: data => adapter.post("/consignments/report", { data }),
    search: search => adapter.post("/consignments/search", { search }),
    show: id => adapter.get(`/consignments/${id}`),
    update: (id, params) =>
      adapter.put(`/consignments/${id}`, { consignment: params })
  },
  engines: {
    search: search => adapter.post("/engines/search", { search })
  },
  session: {
    authenticate: ({ email, password, push_id }) =>
      adapter.post("/session", { email, password, push_id }).then(resp => {
        session.login(resp.data.user, _.get(resp, "data.settings.settings"));
        return resp;
      }),
    verifyPush: ({ email, password, pushIds }) =>
      adapter.post("/session/verify_push", {
        email,
        password,
        push_ids: pushIds
      }),
    logout: () => adapter.delete("/session"),
    parts: () => adapter.get("/session/parts"),
    tenant: () => adapter.get("/session/tenant")
  },
  commercialInvoices: {
    create: data =>
      adapter.post("/commercial_invoices", { commercial_invoice: data })
  },
  commissions: {
    export: params => adapter.get(`/commissions/export?${stringify(params)}`),
    search: params =>
      adapter.post("/commissions/search", {
        search: { ...params, reverse: true }
      }),
    payAll: data =>
      adapter.post("/commissions/pay_all", {
        commissions: data
      }),
    update: (id, data) =>
      adapter.put(`/commissions/${id}`, { commission: data })
  },
  incomingQuotes: {
    customerFind: (id, code) =>
      adapter.get(`/customers/${id}/find_incoming_quote?c=${code}`),
    customerUpdate: (id, data) =>
      adapter.put(`/customers/${id}/incoming_quote`, { incoming_quote: data }),
    export: params =>
      adapter.get(`/incoming_quotes/export?${stringify(params)}`),
    update: (id, data) =>
      adapter.put(`/incoming_quotes/${id}`, { incoming_quote: data }),
    upload: filename =>
      adapter.get("/customers/upload", { params: { filename } }),
    search: params =>
      adapter.post("/incoming_quotes/search", { search: params }),
    send: (id, data) =>
      adapter.post(`/incoming_quotes/${id}/send_pdf`, {
        incoming_quote: data
      }),
    show: (id, presenter = undefined) =>
      adapter.get(
        `/incoming_quotes/${id}${presenter ? `?presenter=${presenter}` : ""}`
      ),
    delete: id => adapter.delete(`/incoming_quotes/${id}`),
    convertToPurchaseOrder: (quoteId, data) =>
      adapter.post(`/incoming_quotes/${quoteId}/purchase_orders/copy_quote`, {
        purchase_order: data
      }),
    lineItems: {
      convertToOutgoingQuote: (quoteId, data) =>
        adapter.post(
          `/incoming_quote_line_items/${quoteId}/convert_to_outgoing_quote`,
          {
            incoming_quote: data
          }
        ),
      create: (id, item) =>
        adapter.post(`/incoming_quotes/${id}/incoming_quote_line_items`, {
          incoming_quote_line_item: item
        }),
      import: (id, data) =>
        adapter.put(`incoming_quotes/${id}/incoming_quote_line_items/import`, {
          incoming_quote: data
        }),
      search: (id, search) =>
        adapter.post(`incoming_quotes/${id}/incoming_quote_line_items/search`, {
          search
        }),
      update: (itemID, item) =>
        adapter.put(`incoming_quote_line_items/${itemID}`, {
          incoming_quote_line_item: item
        }),
      delete: id => adapter.delete(`/incoming_quote_line_items/${id}`)
    }
  },
  integrations: {
    quickbooks: {
      oauth: id =>
        adapter.get(
          `/integrations/quickbooks_oauth/oauth?location_id=${encodeURIComponent(
            id
          )}`
        ),
      redirect: data =>
        adapter.post("/integrations/quickbooks_oauth/redirect", {
          quickbooks: data
        })
    }
  },
  locations: {
    locationSetting: {
      quickbooksLocationSetting: locationId =>
        adapter.get(
          `/locations/${locationId}/location_setting/quickbooks_location_setting`
        ),
      show: locationId =>
        adapter.get(`/locations/${locationId}/location_setting`),
      update: (locationId, params, defaultAccount) =>
        adapter.put(`/locations/${locationId}/location_setting`, {
          location_setting: {
            settings: params,
            default_quickbooks_account: defaultAccount
          }
        })
    },
    quickbooks: {
      accounts: {
        search: (locationId, filter) =>
          adapter.post(`/locations/${locationId}/quickbooks_accounts/search`, {
            search: { filter }
          })
      },
      customers: {
        search: (locationId, filter) =>
          adapter.post(`locations/${locationId}/quickbooks_customers/search`, {
            search: { filter }
          })
      },
      items: {
        search: (locationId, filter, types = null) =>
          adapter.post(`locations/${locationId}/quickbooks_items/search`, {
            search: { filter, types }
          })
      },
      preferences: {
        search: (locationId, filter) =>
          adapter.post(
            `locations/${locationId}/quickbooks_preferences/search`,
            {
              search: { filter }
            }
          )
      },
      resync: locationId =>
        adapter.post(`/quickbooks/locations/${locationId}/resync`),
      vendors: {
        search: (locationId, filter) =>
          adapter.post(`/locations/${locationId}/quickbooks_vendors/search`, {
            search: { filter }
          })
      }
    },
    search: () => adapter.post(`/locations/search`)
  },
  managedRepairOrders: {
    create: data =>
      adapter.post("/managed_repair_orders", { managed_repair_order: data }),
    lineItems: {
      bulkUpdate: (id, data) =>
        adapter.put(`/managed_repair_orders/${id}/line_items/bulk_update`, {
          managed_repair_order: data
        }),
      create: (id, item) =>
        adapter.post(`/managed_repair_orders/${id}/line_items`, {
          line_item: item
        }),
      returnToCustomer: id =>
        adapter.post(
          `/managed_repair_order_line_items/${id}/return_to_customer`
        ),
      update: (id, item) =>
        adapter.put(`/managed_repair_order_line_items/${id}`, {
          line_item: item
        })
    },
    search: params =>
      adapter.post("/managed_repair_order_line_items/search", {
        search: params
      }),
    sync: id => adapter.post(`/managed_repair_orders/${id}/sync`),
    show: id => adapter.get(`/managed_repair_orders/${id}`),
    update: (id, data) =>
      adapter.put(`/managed_repair_orders/${id}`, {
        managed_repair_order: data
      })
  },
  outgoingQuotes: {
    autoFollowup: (id, params) =>
      adapter.post(`/outgoing_quotes/${id}/auto_followup`, {
        outgoing_quote: params
      }),
    create: data => adapter.post("/outgoing_quotes", { outgoing_quote: data }),
    export: params =>
      adapter.get(`/outgoing_quotes/export?${stringify(params)}`),
    update: (id, data) =>
      adapter.put(`/outgoing_quotes/${id}`, { outgoing_quote: data }),
    search: params =>
      adapter.post("/outgoing_quotes/search", { search: params }),
    send: (id, data) =>
      adapter.post(`/outgoing_quotes/${id}/send_pdf`, {
        outgoing_quote: data
      }),
    show: id => adapter.get(`/outgoing_quotes/${id}`),
    lineItems: {
      create: (id, item) =>
        adapter.post(`/outgoing_quotes/${id}/outgoing_quote_line_items`, {
          outgoing_quote_line_item: item
        }),
      import: (id, data) =>
        adapter.put(`outgoing_quotes/${id}/outgoing_quote_line_items/import`, {
          outgoing_quote: data
        }),
      update: (itemId, item) =>
        adapter.put(`outgoing_quote_line_items/${itemId}`, {
          outgoing_quote_line_item: item
        }),
      delete: id => adapter.delete(`outgoing_quote_line_items/${id}`)
    }
  },
  parts: partsApi(adapter),
  password: {
    reset: data => adapter.post("/password/reset", { user: data }),
    forgot: email => adapter.post("/password/forgot", { user: { email } })
  },
  repairOrders: {
    cancel: (id, cancellationReason) =>
      adapter.post(`/repair_orders/${id}/toggle_cancelled`, {
        repair_order: { cancellation_reason: cancellationReason }
      }),
    convert: data =>
      adapter.post("/repair_orders/convert", { repair_order: data }),
    export: params => adapter.get(`/repair_orders/export?${stringify(params)}`),
    find: id => adapter.get(`/repair_orders/${id}/find`),
    update: (id, data) =>
      adapter.put(`/repair_orders/${id}`, { repair_order: data }),
    previewQuote: id => adapter.get(`/repair_orders/${id}/preview_quote_email`),
    previewRequest: id =>
      adapter.get(`/repair_orders/${id}/preview_request_email`),
    search: params => adapter.post("/repair_orders/search", { search: params }),
    sync: id => adapter.post(`/repair_orders/${id}/sync`),
    orderSearch: params =>
      adapter.post("/repair_orders/order_search", { search: params }),
    preview: (id, { type }) =>
      adapter.get(`/repair_orders/${id}/preview_email?type=${type}`),
    send: (id, data) =>
      adapter.post(`/repair_orders/${id}/send_pdf`, { email: data }),
    show: id => adapter.get(`/repair_orders/${id}`),
    sendRepairOrderEmail: (id, data) =>
      adapter.post(`/repair_orders/${id}/send_ro_email`, { email: data }),
    lineItems: {
      approve: (id, data) =>
        adapter.post(
          `/repair_order_line_items/${id}/approve_final_evaluation`,
          { line_item: data }
        ),
      bulkUpdate: (repairOrderId, data) =>
        adapter.post(`/repair_orders/${repairOrderId}/line_items/bulk_update`, {
          repair_order: data
        }),
      cancelVendorRmaRequest: id =>
        adapter.post(
          `/repair_order_line_items/${id}/cancel_vendor_rma_request`
        ),
      cancel: (id, reason) =>
        adapter.put(`/repair_order_line_items/${id}`, {
          line_item: { cancelled: true, cancellation_reason: reason }
        }),
      create: (id, item) =>
        adapter.post(`/repair_orders/${id}/line_items`, { line_item: item }),
      deleteExpectedComponent: id =>
        adapter.delete(
          `/repair_order_line_items/${id}/delete_expected_component`
        ),
      import: (id, data) =>
        adapter.put(`/repair_orders/${id}/line_items/import`, {
          line_item: data
        }),
      removeMainComponent: id =>
        adapter.post(`/repair_order_line_items/${id}/remove_main_component`),
      requiredComponents: {
        create: (id, item) =>
          adapter.post(
            `/repair_order_line_items/${id}/required_component_quotes`,
            { required_component_quote: item }
          ),
        search: (id, params) =>
          adapter.post(
            `/repair_order_line_items/${id}/required_component_quotes/search`,
            {
              search: params
            }
          )
      },
      update: (id, item) =>
        adapter.put(`/repair_order_line_items/${id}`, { line_item: item }),
      search: (id, params) =>
        adapter.post(`/repair_orders/${id}/line_items/search`, {
          search: params
        }),
      shopAssistCostItem: (id, params) =>
        adapter.post(
          `/repair_order_line_items/${id}/shop_assist_cost_savings`,
          { line_item: params }
        ),
      shopAssistItems: params =>
        adapter.post(`/repair_order_line_items/shop_assist_line_items`, {
          search: params
        }),
      transfer: (id, params) =>
        adapter.post(`/repair_order_line_items/${id}/transfer`, {
          line_item: params
        }),
      vendorRmaRequest: (id, params) =>
        adapter.post(`/repair_order_line_items/${id}/vendor_rma_request`, {
          line_item: params
        })
    }
  },
  repairQuotes: {
    create: params => adapter.post("/repair_quotes", { repair_quote: params }),
    delete: id => adapter.delete(`/repair_quotes/${id}`),
    update: (id, params) =>
      adapter.put(`/repair_quotes/${id}`, { repair_quote: params }),
    show: id => adapter.get(`/repair_quotes/${id}`),
    preview: id => adapter.get(`/repair_quotes/${id}/preview_email`),
    send: (id, data) =>
      adapter.post(`/repair_quotes/${id}/send_email`, { email: data })
  },
  returnedConsignment: {
    show: id => adapter.get(`/returned_consignments/${id}`)
  },
  returnToCustomerRecords: {
    show: id => adapter.get(`/return_to_customer_records/${id}`)
  },
  alternates: {
    update: (id, alternate) => adapter.put(`/alternates/${id}`, { alternate }),
    delete: id => adapter.delete(`/alternates/${id}`)
  },
  companies: {
    aircrafts: {
      create: (companyId, params) =>
        adapter.post(`/companies/${companyId}/aircrafts`, { aircraft: params }),
      delete: (companyId, aircraftId) =>
        adapter.delete(`/companies/${companyId}/aircrafts/${aircraftId}`),
      search: (companyId, search) =>
        adapter.post(`/companies/${companyId}/aircrafts/search`, { search })
    },
    create: company => adapter.post("/companies", { company }),
    export: params => adapter.get(`/companies/export?${stringify(params)}`),
    search: params => adapter.post("/companies/search", { search: params }),
    searchWithAddress: params =>
      adapter.post("/companies/search", {
        search: Object.assign({ search_billing_address: true }, params)
      }),
    sendUltimateConsigneeForm: (id, params) =>
      adapter.post(`/companies/${id}/send_ultimate_consignee_form`, {
        email: params
      }),
    show: (id, type = "detail") => adapter.get(`/companies/${id}?type=${type}`),
    update: (id, company) => adapter.put(`/companies/${id}`, { company }),
    contacts: {
      search: (companyId, params) =>
        adapter.post(`companies/${companyId}/contacts/search`, {
          search: Object.assign({ type: "detail" }, params)
        }),
      create: (companyId, contact) =>
        adapter.post(`/companies/${companyId}/contacts`, { contact })
    },
    parts: {
      history: (companyId, partIds) =>
        adapter.get(
          `/companies/${companyId}/parts/history?part_ids=${partIds.join(",")}`
        )
    },
    physicalAddresses: {
      search: (companyId, params) =>
        adapter.post(`companies/${companyId}/physical_addresses/search`, {
          search: Object.assign({ type: "detail" }, params)
        }),
      create: (id, address) =>
        adapter.post(`/companies/${id}/physical_addresses`, {
          physical_address: address
        })
    },
    rankingStats: ({ endDate, startDate }) =>
      adapter.get("companies/ranking_stats", {
        params: { end_date: endDate, start_date: startDate }
      }),
    relatedCompanies: {
      create: (id, relatedCompany) =>
        adapter.post(`/companies/${id}/related_companies`, {
          related_company: relatedCompany
        }),
      search: (companyId, params) =>
        adapter.post(`companies/${companyId}/related_companies/search`, {
          search: params
        })
    },
    shippingMethods: {
      search: (companyId, params) =>
        adapter.post(`companies/${companyId}/shipping_methods/search`, {
          search: Object.assign({ type: "detail" }, params)
        }),
      create: (id, shippingMethod) =>
        adapter.post(`/companies/${id}/shipping_methods`, {
          shipping_method: shippingMethod
        })
    },
    repairStationStatusLastVerified: (id, params) =>
      adapter.put(
        `/companies/${id}/repair_station_status_last_verified_details`,
        { company: params }
      ),
    vendorStatusLastVerified: (id, params) =>
      adapter.put(`/companies/${id}/vendor_status_last_verified_details`, {
        company: params
      })
  },
  users: {
    create: user => adapter.post("/users", { user }),
    enableMfa: id => adapter.post(`/users/${id}/enable_mfa`),
    search: params => adapter.post("/users/search", { search: params }),
    show: id => adapter.get(`/users/${id}`),
    update: (id, user) => adapter.put(`/users/${id}`, { user }),
    requestVerification: id =>
      adapter.post(`/users/${id}/send_verification_request`),
    verifyEmail: (id, data) =>
      adapter.post(`/users/${id}/verify_email`, { user: data })
  },
  notifications: {
    search: (
      params = {
        page: 1,
        limit: 20,
        sort: "id",
        reverse: true,
        filter: true
      }
    ) =>
      adapter.post("/notifications/search", {
        search: params
      }),
    update: (id, notification) =>
      adapter.put(`/notifications/${id}`, { notification }),
    markAllRead: search =>
      adapter.put("/notifications/mark_all_read", { search })
  },
  attachments: {
    list: (id, type) =>
      adapter.post(`${type}/${id}/attachments/search`, {
        search: { limit: 100, sort: "id" }
      }),
    create: (id, type, attachment) =>
      adapter.post(`/${type}/${id}/attachments`, { attachment }),
    upload: filename =>
      adapter.get("/attachments/upload", { params: { filename } }),
    update: (id, attachment) =>
      adapter.put(`/attachments/${id}`, { attachment }),
    destroy: id => adapter.delete(`/attachments/${id}`),
    toS3: (url, file) =>
      axios.put(url, file, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded"
        }
      })
  },
  notes: {
    index: (id, type, notesType) =>
      adapter.post(`/${type}/${id}/notes/search`, {
        search: { limit: 100, notes_type: notesType }
      }),
    create: (id, type, note) => adapter.post(`/${type}/${id}/notes`, { note }),
    update: (id, type, note) =>
      adapter.put(`/${type}/${id}/notes/${note.id}`, { note }),
    delete: (id, type, note) =>
      adapter.delete(`/${type}/${id}/notes/${note.id}`)
  },
  shippingMethods: {
    update: shippingMethod =>
      adapter.put(`shipping_methods/${shippingMethod.id}`, {
        shipping_method: shippingMethod
      }),
    destroy: id => adapter.delete(`shipping_methods/${id}`)
  },
  contacts: {
    list: params => adapter.get("/contacts", { params }),
    update: contact => adapter.put(`contacts/${contact.id}`, { contact }),
    destroy: id => adapter.delete(`/contacts/${id}`)
  },
  customerRmas: {
    cancel: id => adapter.post(`/customer_rmas/${id}/cancel`),
    search: params => adapter.post("/customer_rmas/search", { search: params }),
    export: params => adapter.get(`/customer_rmas/export?${stringify(params)}`),
    send: (id, params) =>
      adapter.post(`/customer_rmas/${id}/send_pdf`, { email: params })
  },
  inventories: {
    applyLotCost: (id, params) =>
      adapter.post(`/inventories/${id}/apply_lot_cost`, { inventory: params }),
    assignExchange: (id, params) =>
      adapter.post(`/inventories/${id}/assign_exchange`, { inventory: params }),
    attachments: {
      search: id =>
        adapter.post(`/inventories/${id}/attachments/search_all`, {
          search: { limit: 50 }
        })
    },
    bulkUpdate: params =>
      adapter.post("inventories/bulk_update", { inventories: params }),
    cancelConsignment: (id, params) =>
      adapter.post(`inventories/${id}/cancel_consignment`, {
        consignment: params
      }),
    deleteInventory: id => adapter.delete(`inventories/${id}`),
    export: params => adapter.get(`/inventories/export?${stringify(params)}`),
    print: id => adapter.post(`inventories/${id}/print`),
    reclaim: id => adapter.post(`inventories/${id}/reclaim`),
    requestScrapAtShop: (id, params) =>
      adapter.post(`inventories/${id}/request_scrap_at_shop`, {
        email: params
      }),
    returnToCustomer: (id, inventory) =>
      adapter.post(`/inventories/${id}/return_to_customer`, { inventory }),
    search: params => adapter.post("/inventories/search", { search: params }),
    stats: params => adapter.post("/inventories/stats", { search: params }),
    statsPerBuyer: () => adapter.post("/inventories/stats_per_buyer"),
    update: (id, inventory) => adapter.put(`/inventories/${id}`, { inventory }),
    show: (id, recordPagerContext = "part", type = "detail") =>
      adapter.get(
        `inventories/${id}?record_pager_context=${recordPagerContext}&presenter_type=${type}`
      ),
    vendorRmas: {
      create: (id, rma) =>
        adapter.post(`inventories/${id}/vendor_rmas`, { rma }),
      search: params => adapter.post("/vendor_rmas/search", { search: params })
    },
    returnToCustomerRecords: {
      create: (id, params) =>
        adapter.post(`inventories/${id}/return_to_customer_records`, {
          return_to_customer_record: params
        })
    },
    transfers: {
      create: (
        id,
        transferToLocationId,
        outgoingAwbNumber,
        specialInstructions
      ) =>
        adapter.post(`/inventories/${id}/transfers`, {
          inventory_transfer_order: {
            transfer_to_location_id: transferToLocationId,
            outgoing_awb_number: outgoingAwbNumber,
            special_instructions: specialInstructions
          }
        }),
      delete: (id, transferId) =>
        adapter.delete(`/inventories/${id}/transfers/${transferId}`)
    },
    reserveForSale: (id, inventory) =>
      adapter.post(`/inventories/${id}/reserve_for_sale`, { inventory }),
    reserveForRepair: id =>
      adapter.post(`/inventories/${id}/reserve_for_repair`),
    split: (id, inventory) =>
      adapter.post(`/inventories/${id}/split`, { inventory }),
    togglePick: (id, params) =>
      adapter.post(`/inventories/${id}/toggle_pick`, { order: params }),
    unreserveForRepair: id =>
      adapter.post(`inventories/${id}/unreserve_repair`),
    unreserveForSale: id => adapter.post(`inventories/${id}/unreserve_sale`),
    costAdjustments: {
      cancel: id => adapter.post(`/cost_adjustments/${id}/cancel`),
      create: (id, costAdjustment) =>
        adapter.post(`/inventories/${id}/cost_adjustments`, {
          cost_adjustment: costAdjustment
        })
    },
    dummy: {
      create: (id, dummy) =>
        adapter.post(`/inventories/${id}/dummy`, { dummy_inventory: dummy }),
      destroy: id => adapter.delete(`/inventories/${id}/dummy`),
      update: (id, dummy) =>
        adapter.put(`/inventories/${id}/dummy`, { dummy_inventory: dummy })
    },
    repairOrdersCart: {
      search: params =>
        adapter.post("/repair_order_cart_items/search", { search: params }),
      create: id => adapter.post(`/inventories/${id}/repair_order_cart_items`),
      delete: id =>
        adapter.delete(`/inventories/${id}/repair_order_cart_items`),
      clearCart: () => adapter.post("/repair_order_cart_items/clear_cart")
    },
    saleOrdersCart: {
      search: params =>
        adapter.post("/sales_order_cart_items/search", { search: params }),
      create: id => adapter.post(`/inventories/${id}/sales_order_cart_items`),
      delete: id => adapter.delete(`/inventories/${id}/sales_order_cart_items`),
      clearCart: () => adapter.post("/sales_order_cart_items/clear_cart")
    },
    searchReservedForRepair: params =>
      adapter.post("/inventories/search", {
        search: {
          ...params,
          presenter_type: "detail",
          repair_status: [RESERVE_FOR_REPAIR, RESERVE_FOR_SHOP_ASSIST].join(
            ","
          ),
          source: "repair_cart",
          sort: "reserved_for_repair_on"
        }
      }),
    searchReservedForSale: params =>
      adapter.post("/inventories/search", {
        search: {
          ...params,
          status: "reserve_for_sale,on_order_for_sale",
          reserved_for_user: true,
          placeholder_reserved_for_sale: true,
          sort: "reserved_for_sale_on"
        }
      }),
    undoScrap: id => adapter.post(`inventories/${id}/undo_scrap`)
  },
  lots: {
    cancel: (id, cancellationReason) =>
      adapter.post(`/lots/${id}/cancel`, {
        lot: { cancellation_reason: cancellationReason }
      }),
    costAdjustments: {
      create: (id, costAdjustment) =>
        adapter.post(`/lots/${id}/lot_cost_adjustments`, {
          lot_cost_adjustment: costAdjustment
        })
    },
    create: lot => adapter.post("/lots", { lot }),
    export: id => adapter.get(`/lots/${id}/export`),
    search: params => adapter.post("/lots/search", { search: params }),
    show: id => adapter.get(`/lots/${id}`),
    sync: id => adapter.post(`/lots/${id}/sync`),
    update: (id, lot) => adapter.put(`/lots/${id}`, { lot }),
    lineItems: {
      create: (id, lineItem) =>
        adapter.post(`lots/${id}/line_items`, { line_item: lineItem }),
      import: (id, data) =>
        adapter.put(`lots/${id}/line_items/import`, { lot: data }),
      search: (id, search) =>
        adapter.post(`lots/${id}/line_items/search`, { search }),
      update: (itemID, item) =>
        adapter.put(`lot_line_items/${itemID}`, { line_item: item })
    },
    receivingRecords: {
      search: (lotId, search) =>
        adapter.post(`/lots/${lotId}/receiving_records/search`, { search })
    },
    setTraceHold: (id, traceApproval) =>
      adapter.post(`/lots/${id}/set_trace_hold`, {
        lot: { trace_approved: traceApproval }
      })
  },
  mistakeShipments: {
    search: params =>
      adapter.post("/mistake_shipments/search", {
        search: params
      }),
    show: id => adapter.get(`/mistake_shipments/${id}`),
    update: (id, data) =>
      adapter.put(`/mistake_shipments/${id}`, { mistake_shipment: data })
  },
  physicalAddresses: {
    update: physicalAddress =>
      adapter.put(`addresses/${physicalAddress.id}`, { ...physicalAddress }),
    destroy: id => adapter.delete(`/addresses/${id}`)
  },
  purchaseOrders: {
    create: purchaseOrder =>
      adapter.post("/purchase_orders", { purchase_order: purchaseOrder }),
    search: params =>
      adapter.post("/purchase_orders/search", { search: params }),
    setTraceHold: (id, traceApproval) =>
      adapter.post(`/purchase_orders/${id}/set_trace_hold`, {
        purchase_order: { trace_approved: traceApproval }
      }),
    show: id => adapter.get(`/purchase_orders/${id}`),
    sync: id => adapter.post(`/purchase_orders/${id}/sync`),
    update: (id, purchaseOrder) =>
      adapter.put(`/purchase_orders/${id}`, { purchase_order: purchaseOrder }),
    updateQcStatus: (id, params) =>
      adapter.post(`/purchase_orders/${id}/update_qc_status`, {
        purchase_order: params
      }),
    preview: (id, params) =>
      adapter.get(
        `/purchase_orders/${id}/preview_email?${new URLSearchParams(
          params
        ).toString()}`
      ),
    send: (id, data) =>
      adapter.post(`/purchase_orders/${id}/send_pdf`, { email: data }),
    sendNotice: (id, data) =>
      adapter.post(`/purchase_orders/${id}/send_cancellation_notice`, {
        email: data
      }),
    cancel: (id, cancellationReason) =>
      adapter.post(`/purchase_orders/${id}/toggle_cancelled`, {
        purchase_order: { cancellation_reason: cancellationReason }
      }),
    lineItems: {
      bulkUpdate: (purchaseOrderId, params) =>
        adapter.post(
          `/purchase_orders/${purchaseOrderId}/line_items/bulk_update`,
          { purchase_order: params }
        ),
      create: (id, item) =>
        adapter.post(`/purchase_orders/${id}/line_items`, { line_item: item }),
      exchange: id => adapter.post(`/purchase_order_line_items/${id}/exchange`),
      exchangeReversal: id =>
        adapter.post(`/purchase_order_line_items/${id}/exchange_reversal`),
      export: params =>
        adapter.get(`/purchase_order_line_items/export?${stringify(params)}`),
      outright: id => adapter.post(`/purchase_order_line_items/${id}/outright`),
      update: (itemID, item) =>
        adapter.put(`purchase_order_line_items/${itemID}`, { line_item: item })
    },
    receivedCores: {
      search: (purchaseOrderId, search) =>
        adapter.post(
          `/purchase_orders/${purchaseOrderId}/received_cores/search`,
          { search }
        )
    },
    receivingRecords: {
      search: (purchaseOrderId, search) =>
        adapter.post(
          `/purchase_orders/${purchaseOrderId}/receiving_records/search`,
          { search }
        )
    }
  },
  purchaseOrderLineItems: {
    assignableExchangeSearch: () =>
      adapter.post(`/purchase_order_line_items/assignable_exchange_search`)
  },
  receivingRecord: {
    create: receivingRecord =>
      adapter.post("/receiving_records", {
        receiving_record: receivingRecord
      }),
    export: params =>
      adapter.get(`/receiving_records/export?${stringify(params)}`),
    save: (id, params) =>
      adapter.put(`/receiving_records/${id}/save`, {
        receiving_record: params
      }),
    update: (id, receivingRecord) =>
      adapter.put(`/receiving_records/${id}`, {
        receiving_record: receivingRecord
      }),
    updateQcStatus: (id, params) =>
      adapter.put(`/receiving_records/${id}/update_qc_status`, {
        receiving_record: params
      }),
    show: id => adapter.get(`/receiving_records/${id}`),
    search: params =>
      adapter.post("/receiving_records/search", {
        search: { sort: "created_at", ...params, reverse: true }
      }),
    unreceive: id => adapter.post(`/receiving_records/${id}/unreceive`)
  },
  rejectedCores: {
    search: params =>
      adapter.post("/rejected_cores/search", {
        search: params
      }),
    show: id => adapter.get(`/rejected_cores/${id}`),
    update: (id, data) =>
      adapter.put(`/rejected_cores/${id}`, { rejected_core: data })
  },
  relatedCompanies: {
    destroy: id => adapter.delete(`/related_companies/${id}`),
    update: (id, relatedCompany) =>
      adapter.put(`related_companies/${id}`, {
        related_company: relatedCompany
      })
  },
  rmas: {
    search: params => adapter.post("/rmas/search", { search: params }),
    show: id => adapter.get(`/rmas/${id}`),
    sync: id => adapter.post(`/rmas/${id}/sync`),
    update: (id, data) => adapter.put(`/rmas/${id}`, { rma: data })
  },
  shipments: {
    create: shipment => adapter.post("/shipments", { shipment }),
    export: params => adapter.get(`/shipments/export?${stringify(params)}`),
    lineItems: {
      search: (id, params) =>
        adapter.post(`/shipments/${id}/line_items/search`, { search: params })
    },
    reversal: id => adapter.post(`/shipments/${id}/reversal`),
    search: params => adapter.post("/shipments/search", { search: params }),
    show: id => adapter.get(`/shipments/${id}`),
    stats: () => adapter.get("/shipments/stats"),
    toggleCheck: id => adapter.post(`/shipments/${id}/toggle_check`),
    update: (id, data) => adapter.put(`/shipments/${id}`, { shipment: data })
  },
  tenant: {
    create: () => adapter.post("/tenant"),
    find: () => adapter.get("/tenant")
  },
  transfers: {
    show: id => adapter.get(`/transfers/${id}`)
  },
  vendorRmas: {
    cancel: id => adapter.post(`/vendor_rmas/${id}/cancel`),
    search: params => adapter.post("/vendor_rmas/search", { search: params }),
    update: (id, data) => adapter.put(`/vendor_rmas/${id}`, { rma: data }),
    export: params => adapter.get(`/vendor_rmas/export?${stringify(params)}`)
  },
  discrepancy: {
    create: (id, bulk) =>
      adapter.post(`receiving_records/${id}/discrepancies`, { bulk }),
    update: (discrepancyId, discrepancy) =>
      adapter.put(`discrepancies/${discrepancyId}`, discrepancy),
    resolve: (id, data) => adapter.post(`discrepancies/${id}/resolve`, data)
  },
  orders: {
    search: params => adapter.post("/orders/search", { search: params }),
    warehouse_pick: (id, order) =>
      adapter.put(`/orders/${id}/warehouse_pick`, { order })
  },
  salesCart: {
    show: () => adapter.get("/sales_cart")
  },
  salesOrders: {
    cachedShow: id => adapter.get(`/sales_orders/${id}/cached_show`),
    cancellationReversal: id =>
      adapter.post(`sales_orders/${id}/cancellation_reversal`),
    cocSearch: id => adapter.post(`sales_orders/${id}/coc_search`),
    commercialInvoice: {
      save: (id, data) =>
        adapter.post(`sales_orders/${id}/commercial_invoice/save`, {
          commercial_invoice: data
        })
    },
    convert: data =>
      adapter.post("/sales_orders/convert", { sales_order: data }),
    convertFromOutgoingQuote: data =>
      adapter.post("/sales_orders/convert_from_outgoing_quote", {
        sales_order: data
      }),
    convertWithDummy: data =>
      adapter.post("/sales_orders/convert_with_dummy", { sales_order: data }),
    createCoc: (id, params) =>
      adapter.post(`/sales_orders/${id}/create_certificate_of_conformance`, {
        sales_order: params
      }),
    search: params => adapter.post("/sales_orders/search", { search: params }),
    show: (id, presenter = undefined) =>
      adapter.get(
        `/sales_orders/${id}${presenter ? `?presenter=${presenter}` : ""}`
      ),
    sync: id => adapter.post(`/sales_orders/${id}/sync`),
    syncCoreRepairCharges: id =>
      adapter.post(`/sales_orders/${id}/sync_core_repair_charges`),
    update: (id, data) =>
      adapter.put(`/sales_orders/${id}`, { sales_order: data }),
    lineItems: {
      bulkUpdate: (salesOrderId, params) =>
        adapter.post(`/sales_orders/${salesOrderId}/line_items/bulk_update`, {
          sales_order: params
        }),
      cocPermissions: id =>
        adapter.get(`/sales_order_line_items/${id}/coc_permissions`),
      export: params =>
        adapter.get(`/sales_order_line_items/export?${stringify(params)}`),
      search: (salesOrderId, params) =>
        adapter.post(`/sales_orders/${salesOrderId}/line_items/search`, {
          search: params
        }),
      sendExchangeAgreement: id =>
        adapter.post(
          `/sales_order_line_items/${id}/send_exchange_agreement_pdf`
        ),
      sendQualityControlDocuments: (id, params) =>
        adapter.post(
          `/sales_order_line_items/${id}/send_quality_control_documents_pdf`,
          {
            sales_order: params
          }
        ),
      cancel: (id, reason, sendCancellationEmail) =>
        adapter.put(`/sales_order_line_items/${id}`, {
          line_item: {
            cancelled: true,
            cancellation_reason: reason,
            send_cancellation_email: sendCancellationEmail
          }
        }),
      cancelConsignment: id =>
        adapter.post(`/sales_order_line_items/${id}/cancel_consignment`),
      returnCoreToCustomer: id =>
        adapter.post(`/sales_order_line_items/${id}/return_core`),
      cancelOutrightSale: id =>
        adapter.post(`/sales_order_line_items/${id}/cancel_outright_sale`),
      cancelSubsequentExchange: id =>
        adapter.post(
          `/sales_order_line_items/${id}/cancel_subsequent_exchange`
        ),
      cancellationReversal: id =>
        adapter.post(`/sales_order_line_items/${id}/cancellation_reversal`),
      convertToExchange: (id, data) =>
        adapter.post(`/sales_order_line_items/${id}/convert_to_exchange`, {
          line_item: data
        }),
      exchange: (id, data) =>
        adapter.post(`/sales_order_line_items/${id}/exchange`, {
          line_item: data
        }),
      exchangeReversal: id =>
        adapter.post(`/sales_order_line_items/${id}/exchange_reversal`),
      internalOutrightSale: (id, data) =>
        adapter.post(`sales_order_line_items/${id}/internal_outright_sale`, {
          line_item: data
        }),
      outrightSale: (id, data) =>
        adapter.post(`sales_order_line_items/${id}/outright_sale`, {
          line_item: data
        }),
      create: (salesOrderId, data) =>
        adapter.post(`/sales_orders/${salesOrderId}/line_items`, {
          line_item: data
        }),
      update: (id, data) =>
        adapter.put(`/sales_order_line_items/${id}`, { line_item: data }),
      customerRmas: {
        create: (id, data) =>
          adapter.post(`/sales_order_line_items/${id}/customer_rmas`, {
            rma: data
          })
      }
    },
    send: id => adapter.post(`/sales_orders/${id}/send_pdf`),
    sendWeightDims: (id, data) =>
      adapter.post(`/sales_orders/${id}/send_weight_dims_pdf`, {
        sales_order: data
      }),
    cancel: (id, params) =>
      adapter.post(`/sales_orders/${id}/toggle_cancelled`, {
        sales_order: params
      })
  },
  reports: {
    buyer_stats: {
      search: params => adapter.post("/buyer_stats/search", { search: params })
    },
    purchases_report: {
      search: params =>
        adapter.post("/purchases_report/search", { search: params })
    },
    sales_report: {
      search: params => adapter.post("/sales_report/search", { search: params })
    },
    depreciations: {
      search: (startTime, endTime, locationId) =>
        adapter.post(`/monthly_depreciations/search`, {
          search: {
            start_time: startTime,
            end_time: endTime,
            location_id: locationId
          }
        }),
      searchPerLocation: (startTime, endTime) =>
        adapter.post("/monthly_depreciations/search_per_location", {
          search: {
            start_time: startTime,
            end_time: endTime
          }
        })
    },
    purchases: {
      search: (startTime, endTime, locationId) =>
        adapter.post(`/monthly_purchases/search`, {
          search: {
            start_time: startTime,
            end_time: endTime,
            location_id: locationId
          }
        }),
      searchPerLocation: (startTime, endTime) =>
        adapter.post("/monthly_purchases/search_per_location", {
          search: {
            start_time: startTime,
            end_time: endTime
          }
        })
    },
    sales: {
      search: (startTime, endTime) =>
        adapter.post(`/monthly_sales/search`, {
          search: { start_time: startTime, end_time: endTime }
        })
    },
    part_stats: {
      search: id => adapter.get(`/parts/${id}/stats`)
    },
    company_stats: {
      search: id => adapter.get(`/companies/${id}/stats`)
    },
    high_scores: {
      sales_stats: params =>
        adapter.post(`/reports/sales_stats`, {
          search: params
        })
    },
    search: params => adapter.post("/reports/search", { search: params })
  },
  restrictedEntities: {
    addressSearch: params =>
      adapter.post("/restricted_entities/address_search", { search: params }),
    search: params =>
      adapter.post("/restricted_entities/search", { search: params })
  },
  outboundOrders: {
    export: params =>
      adapter.get(`/outbound_orders/export?${stringify(params)}`),
    search: params =>
      adapter.post("/outbound_orders/search", { search: params })
  },
  shoppingList: {
    search: params => adapter.post("/shopping_lists/search", { search: params })
  },
  dashboard: () => adapter.get("/dashboard"),
  quarterlyGoals: {
    create: data => adapter.post("/quarterly_goals", { quarterly_goal: data }),
    update: (id, data) =>
      adapter.put(`/quarterly_goals/${id}`, { quarterly_goal: data }),
    search: params =>
      adapter.post("/quarterly_goals/search", { search: params })
  }
};



// WEBPACK FOOTER //
// ./app/api.js